







































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import MixinDialog from '@/plugins/podolib/services/helper/mixins/dialog';
import { ValidationProvider } from 'vee-validate/dist/types/components/Provider';

import PatientAcquisitionPainLevel from '@/components/patients/acquisition/PatientAcquisitionPainLevel.vue';

import { TForm } from '@/plugins/podolib/lib/types';
import {
  ConditionsPain,
  ConditionsPainLocations,
  ConditionsPainSide,
  ConditionsPainZone_1,
  ConditionsPainZone_2_C1,
  ConditionsPainZone_2_C2,
  StepConditions,
} from '@/components/analysis/steps/conditions/AnalysisStepConditions';
import { Severity } from '@sentry/types';
import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { TAcquisition } from '@/plugins/podolib/services/entities/EntityPatients';

type TFields = {
  location: {
    value: ConditionsPain['location'];
  };
  side: {
    value: ConditionsPain['side'];
  };
  level: {
    value: ConditionsPain['level'];
  };
  zone_1: {
    value: ConditionsPain['zone_1'];
  };
  zone_2: {
    value: ConditionsPain['zone_2'];
  };
};

@Component({
  components: {
    PatientAcquisitionPainLevel,
  },
})
export default class AnalysisStepConditionsPainsDialog extends MixinDialog {
  $refs!: {
    observer: InstanceType<typeof ValidationProvider>;
    form: InstanceType<typeof Vue> & { reset: () => void };
  };

  @Prop({ required: true }) private acquisitionId!: TEntity<TAcquisition>['_id'];
  @Prop({ required: true }) private pains!: StepConditions['pains'];
  @Prop(Boolean) private inResults!: boolean;

  mode: 'add' | 'edit' | null = null;

  index: number | null = null;
  patientPainId: string | null = null;

  form: TForm<TFields> = {
    fields: {
      location: {
        value: null!,
      },
      side: {
        value: null!,
      },
      level: {
        value: null!,
      },
      zone_1: {
        value: null!,
      },
      zone_2: {
        value: null!,
      },
    },
    pending: false,
    error: null,
    success: false,
  };

  get items() {
    let location;
    let side;
    let zone_1;
    let zone_2;

    location = this.$pdc.helper!.enumToArrayAndTranslate(ConditionsPainLocations, {
      key: 'analysis.steps.conditions.pains.location',
    });
    side = this.$pdc.helper!.enumToArrayAndTranslate(ConditionsPainSide, {
      key: 'analysis.steps.conditions.pains.side',
    });
    zone_1 = this.$pdc.helper!.enumToArrayAndTranslate(ConditionsPainZone_1, {
      key: 'analysis.steps.conditions.pains.zone_1',
    });

    switch (this.form.fields.location.value) {
      case ConditionsPainLocations.Foot:
        zone_2 = this.$pdc.helper!.enumToArrayAndTranslate(ConditionsPainZone_2_C2, {
          key: 'analysis.steps.conditions.pains.zone_2',
        });
        break;
      default:
        zone_2 = this.$pdc.helper!.enumToArrayAndTranslate(ConditionsPainZone_2_C1, {
          key: 'analysis.steps.conditions.pains.zone_2',
        });
        break;
    }

    return {
      location,
      side,
      zone_1,
      zone_2,
    };
  }

  @Watch('items', { deep: true })
  onItemsChanged() {
    this.$nextTick(() => {
      if (
        this.form.fields.zone_2.value &&
        !Object.values(this.items.zone_2).find((x) => x.value === this.form.fields.zone_2.value)
      ) {
        this.form.fields.zone_2.value = null!;
      }
    });
  }
  setSelectedPainLevel(painLevel: number) {
    this.form.fields.level.value = painLevel;
  }

  save() {
    switch (this.mode) {
      case 'add':
        return this.createPain();
      case 'edit':
        return this.patchPain();
    }
  }

  async createPain() {
    try {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      // Down 'success' flag
      this.form.success = false;
      // Reset 'error'
      this.form.error = null;
      // Set 'pending' flag
      this.form.pending = true;

      const data = {
        location: this.form.fields.location.value,
        side: this.form.fields.side.value,
        zone_1: this.form.fields.zone_1.value,
        zone_2: this.form.fields.zone_2.value,
        level: this.form.fields.level.value,
      };

      if (!this.inResults) {
        this.$emit('update:pains', [...this.pains, data]);
      } else {
        this.$pdc.repository?.patchRessource({
          ressource: 'acquisitions',
          params: {
            _id: this.acquisitionId,
          },
          data: {
            $addToSet: {
              'conditions.pains': data,
            },
          },
        });
      }

      this._close();
    } catch (e) {
      // Reset 'error'
      this.form.error = e;
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Error);
    } finally {
      // Down 'pending' flag
      this.form.pending = false;
    }
  }

  async patchPain() {
    try {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      // Reset flags
      this.form.success = false;
      this.form.error = null;
      // Set 'pending' flag
      this.form.pending = true;

      const data = {
        location: this.form.fields.location.value,
        side: this.form.fields.side.value,
        zone_1: this.form.fields.zone_1.value,
        zone_2: this.form.fields.zone_2.value,
        level: this.form.fields.level.value,
      };

      if (!this.inResults) {
        this.$emit(
          'update:pains',
          this.pains.map((value, index) => {
            if (index === this.index) {
              return data;
            }
            return value;
          }),
        );
      } else {
        this.$pdc.repository?.patchRessource({
          ressource: 'acquisitions',
          params: {
            _id: this.acquisitionId,
          },
          data: {
            $set: {
              'conditions.pains.$.location': data.location,
              'conditions.pains.$.side': data.side,
              'conditions.pains.$.zone_1': data.zone_1,
              'conditions.pains.$.zone_2': data.zone_2,
              'conditions.pains.$.level': data.level,
            },
          },
          additionnalQuery: {
            'conditions.pains._id': this.patientPainId,
          },
        });
      }

      this._close();
    } catch (e) {
      // Set 'error' flag
      this.form.error = e;
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Error);
    } finally {
      // Reset 'pending' flag
      this.form.pending = false;
    }
  }

  _open(mode: 'add' | 'edit', settings?: { index?: number; id?: string }) {
    this.mode = mode;

    if (this.mode === 'edit') {
      if (typeof settings?.index === 'number') {
        this.index = Number.isInteger(settings.index) ? settings.index! : null;

        this.form.fields.location.value = this.pains[settings.index!].location;
        this.form.fields.side.value = this.pains[settings.index!].side;
        this.form.fields.zone_1.value = this.pains[settings.index!].zone_1;
        this.form.fields.zone_2.value = this.pains[settings.index!].zone_2;
        this.form.fields.level.value = this.pains[settings.index!].level;
      } else if (settings?.id) {
        this.patientPainId = settings.id;
        const currentPatientPain = this.pains.find((x: any) => x._id === settings.id);

        this.form.fields.location.value = currentPatientPain!.location;
        this.form.fields.side.value = currentPatientPain!.side;
        this.form.fields.zone_1.value = currentPatientPain!.zone_1;
        this.form.fields.zone_2.value = currentPatientPain!.zone_2;
        this.form.fields.level.value = currentPatientPain!.level;
      }
    }

    this.open();
  }

  _close() {
    this.mode = null;
    this.patientPainId = null;

    this.close();
  }

  mounted() {
    this.$pdc.bus!.on('analysis:conditions-pains:add', this._open);
    this.$pdc.bus!.on('analysis:conditions-pains:edit', this._open);
  }
  beforeDestroy() {
    this.$pdc.bus!.off('analysis:conditions-pains:add', this._open);
    this.$pdc.bus!.off('analysis:conditions-pains:edit', this._open);
  }
}
